// src/context/ThemeContext.jsx
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeContext = createContext();

const getDesignTokens = (mode) => ({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          primary: {
            main: '#0891B2',
            dark: '#0E7490',
            light: '#22D3EE',
            50: '#ECFEFF',
            100: '#CFFAFE',
          },
          secondary: {
            main: '#7C3AED',
            light: '#A78BFA',
            dark: '#6D28D9',
          },
          background: {
            default: '#F0F4F8',
            paper: '#FFFFFF',
            subtle: '#E8EEF4',
          },
          text: {
            primary: '#1E293B',
            secondary: '#64748B',
          },
          success: {
            main: '#059669',
            light: '#D1FAE5',
            dark: '#047857',
          },
          error: {
            main: '#E11D48',
            light: '#FFE4E6',
            dark: '#BE123C',
          },
          warning: {
            main: '#D97706',
            light: '#FEF3C7',
            dark: '#B45309',
          },
          info: {
            main: '#0891B2',
            light: '#ECFEFF',
          },
          divider: 'rgba(15, 23, 42, 0.08)',
          action: {
            hover: 'rgba(8, 145, 178, 0.04)',
            selected: 'rgba(8, 145, 178, 0.08)',
          },
        }
      : {
          primary: {
            main: '#22D3EE',
            dark: '#06B6D4',
            light: '#67E8F9',
            50: 'rgba(34, 211, 238, 0.08)',
            100: 'rgba(34, 211, 238, 0.15)',
          },
          secondary: {
            main: '#A78BFA',
            light: '#C4B5FD',
            dark: '#7C3AED',
          },
          background: {
            default: '#0F172A',
            paper: '#1E293B',
            subtle: '#162032',
          },
          text: {
            primary: '#E2E8F0',
            secondary: '#94A3B8',
          },
          success: {
            main: '#34D399',
            light: '#064E3B',
            dark: '#10B981',
          },
          error: {
            main: '#FB7185',
            light: '#4C0519',
            dark: '#F43F5E',
          },
          warning: {
            main: '#FBBF24',
            light: '#451A03',
            dark: '#F59E0B',
          },
          info: {
            main: '#22D3EE',
            light: 'rgba(34, 211, 238, 0.1)',
          },
          divider: 'rgba(148, 163, 184, 0.12)',
          action: {
            hover: 'rgba(34, 211, 238, 0.06)',
            selected: 'rgba(34, 211, 238, 0.12)',
          },
        }),
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { fontWeight: 500, textTransform: 'none' },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        body: {
          scrollbarColor: theme.palette.mode === 'dark' ? '#475569 #1E293B' : '#CBD5E1 #F0F4F8',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: theme.palette.mode === 'dark' ? '#475569' : '#CBD5E1',
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.mode === 'dark' ? '#1E293B' : '#F0F4F8',
          },
        },
      }),
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 12,
          border: theme.palette.mode === 'light'
            ? '1px solid rgba(15, 23, 42, 0.06)'
            : '1px solid rgba(148, 163, 184, 0.08)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 2px 8px rgba(0, 0, 0, 0.4)'
            : '0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04)',
          backgroundImage: 'none',
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          padding: '8px 20px',
        },
        contained: ({ theme }) => ({
          boxShadow: theme.palette.mode === 'light'
            ? '0 1px 3px rgba(8, 145, 178, 0.2), 0 1px 2px rgba(8, 145, 178, 0.12)'
            : '0 2px 4px rgba(0, 0, 0, 0.3)',
          '&:hover': {
            boxShadow: theme.palette.mode === 'light'
              ? '0 4px 12px rgba(8, 145, 178, 0.25)'
              : '0 4px 8px rgba(0, 0, 0, 0.4)',
          },
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: theme.palette.mode === 'light'
              ? '#FFFFFF'
              : theme.palette.background.paper,
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 10,
          ...(theme.palette.mode === 'light' && {
            border: '1px solid',
          }),
        }),
        standardSuccess: ({ theme }) => ({
          ...(theme.palette.mode === 'light' && {
            backgroundColor: '#D1FAE5',
            borderColor: '#A7F3D0',
            color: '#065F46',
          }),
        }),
        standardError: ({ theme }) => ({
          ...(theme.palette.mode === 'light' && {
            backgroundColor: '#FFE4E6',
            borderColor: '#FECDD3',
            color: '#9F1239',
          }),
        }),
        standardInfo: ({ theme }) => ({
          ...(theme.palette.mode === 'light' && {
            backgroundColor: '#ECFEFF',
            borderColor: '#CFFAFE',
            color: '#155E75',
          }),
        }),
        standardWarning: ({ theme }) => ({
          ...(theme.palette.mode === 'light' && {
            backgroundColor: '#FEF3C7',
            borderColor: '#FDE68A',
            color: '#92400E',
          }),
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundImage: 'none',
          ...(theme.palette.mode === 'light' && {
            border: '1px solid rgba(15, 23, 42, 0.06)',
          }),
        }),
      },
    },
  },
});

const getInitialMode = () => {
  const saved = localStorage.getItem('themeMode');
  if (saved) return saved;
  
  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

export const ThemeContextProvider = ({ children }) => {
  const [mode, setMode] = useState(getInitialMode);

  const isDark = mode === 'dark';

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      if (!localStorage.getItem('themeMode')) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', next);
      return next;
    });
  };

  const setTheme = (newMode) => {
    localStorage.setItem('themeMode', newMode);
    setMode(newMode);
  };

  const resetToSystemTheme = () => {
    localStorage.removeItem('themeMode');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setMode(systemPrefersDark ? 'dark' : 'light');
  };

  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  const colors = useMemo(() => {
    const chipColors = isDark ? {
      default: { bg: '#334155', text: '#E2E8F0' },
      primary: { bg: 'rgba(34, 211, 238, 0.15)', text: '#22D3EE' },
      success: { bg: 'rgba(52, 211, 153, 0.15)', text: '#34D399' },
      warning: { bg: 'rgba(251, 191, 36, 0.15)', text: '#FBBF24' },
      error: { bg: 'rgba(251, 113, 133, 0.15)', text: '#FB7185' },
    } : {
      default: { bg: '#E8EEF4', text: '#334155' },
      primary: { bg: '#ECFEFF', text: '#0E7490' },
      success: { bg: '#D1FAE5', text: '#047857' },
      warning: { bg: '#FEF3C7', text: '#B45309' },
      error: { bg: '#FFE4E6', text: '#BE123C' },
    };

    const semanticColors = isDark ? {
      success: '#34D399',
      error: '#FB7185',
      warning: '#FBBF24',
      info: '#22D3EE',
    } : {
      success: '#059669',
      error: '#E11D48',
      warning: '#D97706',
      info: '#0891B2',
    };

    const surfaceColors = isDark ? {
      primary: '#1E293B',
      secondary: '#334155',
      elevated: '#475569',
    } : {
      primary: '#FFFFFF',
      secondary: '#F0F4F8',
      elevated: '#FFFFFF',
    };

    return {
      chip: chipColors,
      semantic: semanticColors,
      surface: surfaceColors,
      getCategoryCard: (color) => ({
        background: isDark ? '#1E293B' : '#FFFFFF',
        border: color || (isDark ? '#22D3EE' : '#0891B2'),
      }),
    };
  }, [isDark]);

  const value = {
    mode,
    isDark,
    toggleTheme,
    setTheme,
    resetToSystemTheme,
    colors,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeContextProvider');
  }
  return context;
};

export default ThemeContext;